import { useState, useCallback } from 'react';

/**
 * Hook générique pour les mutations Supabase (insert, update, delete).
 *
 * Centralise la gestion d'erreur et le loading state.
 * Retourne un boolean pour la logique conditionnelle post-mutation.
 *
 * @param mutationFn Fonction exécutant la mutation Supabase
 * @param options.onSuccess Callback appelé après une mutation réussie (ex: refresh)
 *
 * @example
 * const { execute: createEmployee, loading } = useMutation(
 *   (payload: Partial<Employee>) => supabase.from('employees').insert(payload),
 *   { onSuccess: refresh }
 * );
 * const ok = await createEmployee({ full_name: 'Test' });
 * if (ok) resetForm();
 */
export function useMutation<TArgs = void>(
  mutationFn: (args: TArgs) => PromiseLike<{ error: any }>,
  options?: { onSuccess?: () => void | Promise<void> }
): {
  execute: (args: TArgs) => Promise<boolean>;
  loading: boolean;
  error: Error | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const execute = useCallback(async (args: TArgs): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      const { error: mutationError } = await mutationFn(args);
      if (mutationError) throw mutationError;
      if (options?.onSuccess) {
        await options.onSuccess();
      }
      return true;
    } catch (err) {
      const e = err instanceof Error ? err : new Error(
        typeof err === 'object' && err !== null && 'message' in err ? (err as any).message : String(err)
      );
      console.error('[useMutation]', e.message);
      setError(e);
      return false;
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutationFn]);

  return { execute, loading, error };
}
